'use strict';

import { isEnabled } from './flags.js';

const FEATURE = 'dark-mode';
const STORAGE_KEY = 'rmt-theme';
const TOGGLE_ID = 'theme-toggle';
const VALID_THEMES = new Set(['light', 'dark']);

function getToggleButton() {
  return document.getElementById(TOGGLE_ID);
}

function normalizeTheme(value) {
  if (value && VALID_THEMES.has(value)) return value;
  return 'light';
}

export function getStoredTheme() {
  try {
    return normalizeTheme(globalThis.localStorage?.getItem(STORAGE_KEY));
  } catch {
    return 'light';
  }
}

function setStoredTheme(theme) {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage may be unavailable (private mode, quota). Ignore silently.
  }
}

function updateToggleButton(theme) {
  const button = getToggleButton();
  if (!button) return;

  if (theme === 'dark') {
    button.textContent = '☀️ Light';
    button.setAttribute('aria-label', 'Switch to light mode');
    button.setAttribute('title', 'Switch to light mode');
    button.setAttribute('aria-pressed', 'true');
  } else {
    button.textContent = '🌙 Dark';
    button.setAttribute('aria-label', 'Switch to dark mode');
    button.setAttribute('title', 'Switch to dark mode');
    button.setAttribute('aria-pressed', 'false');
  }
}

export function isDarkModeEnabled() {
  return isEnabled(FEATURE);
}

export function getCurrentTheme() {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/**
 * Apply a theme immediately. The active theme is surfaced on
 * `document.documentElement` as `data-theme="dark"`; light is the absence of
 * the attribute so the default first paint stays light.
 */
export function applyTheme(theme) {
  const normalized = normalizeTheme(theme);
  const root = document.documentElement;

  if (normalized === 'dark') {
    root.setAttribute('data-theme', 'dark');
  } else {
    root.removeAttribute('data-theme');
  }

  updateToggleButton(normalized);
}

/**
 * Toggle between light and dark, persist the new choice, and return it.
 */
export function toggleTheme() {
  const next = getCurrentTheme() === 'dark' ? 'light' : 'dark';
  applyTheme(next);
  setStoredTheme(next);
  return next;
}

function createToggle() {
  if (getToggleButton()) return;

  const topbar = document.querySelector('.topbar');
  if (!topbar) return;

  const button = document.createElement('button');
  button.id = TOGGLE_ID;
  button.className = 'theme-toggle';
  button.type = 'button';

  button.addEventListener('click', () => {
    toggleTheme();
  });

  topbar.appendChild(button);
  updateToggleButton(getCurrentTheme());
}

function removeToggle() {
  const button = getToggleButton();
  if (button) button.remove();
}

/**
 * Initialize dark mode. If the feature flag is off, the toggle is removed and
 * the theme stays light. Otherwise a toggle is injected into the top nav and
 * any stored preference is applied after the default light first paint.
 */
export function initDarkMode() {
  if (!isDarkModeEnabled()) {
    removeToggle();
    applyTheme('light');
    return;
  }

  createToggle();

  // Default first paint is already light. Read the stored value and, if the
  // user previously chose dark, apply it now. This intentionally accepts a
  // brief flash so the app never renders a dark first paint before JS runs.
  const stored = getStoredTheme();
  if (stored === 'dark') {
    applyTheme('dark');
  } else {
    applyTheme('light');
  }
}
